import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { EditorToolbar, SplitLayout } from "@/components/docs/EditorChrome";
import { LetterTemplate } from "@/components/docs/Templates";
import { PaperPreview } from "@/components/docs/PaperPreview";
import { StylePicker } from "@/components/docs/StylePicker";
import { PricingDialog } from "@/components/PricingDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDocEditor } from "@/lib/useDocEditor";

const TONES = [["professional", "Professional"], ["warm", "Warm"], ["confident", "Confident"]];

const LetterForm = ({ api, data, onChange }) => {
  const [resumes, setResumes] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => onChange({ ...data, [k]: v });

  useEffect(() => {
    api.get("/documents").then((r) => setResumes(r.data.filter((d) => d.type === "resume")));
  }, [api]);

  const generate = async () => {
    setBusy(true);
    try {
      const resume = data.resume_id ? (await api.get(`/documents/${data.resume_id}`)).data.data : {};
      const sender = { name: resume.name || data.sender?.name || "", email: resume.email || "", phone: resume.phone || "", location: resume.location || "" };
      let base = { ...data, sender, content: "" };
      onChange(base);
      const full = await api.stream("/ai/cover-letter",
        { resume, job_posting: data.job_posting, tone: data.tone, company: data.company, role: data.role },
        (t) => onChange({ ...base, content: t }));
      onChange({ ...base, content: full.trim() });
      toast.success("Cover letter written");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="cover-letter-form">
      <div className="rounded-xl border border-paper-border bg-white p-5">
        <h3 className="font-serif text-lg text-ink">The job</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><Label className="text-xs font-semibold text-ink-soft">Company</Label>
            <Input className="field mt-1.5" value={data.company} onChange={(e) => set("company")(e.target.value)} data-testid="letter-company-input" /></div>
          <div><Label className="text-xs font-semibold text-ink-soft">Role</Label>
            <Input className="field mt-1.5" value={data.role} onChange={(e) => set("role")(e.target.value)} data-testid="letter-role-input" /></div>
        </div>
        <Label className="mt-4 block text-xs font-semibold text-ink-soft">Job posting</Label>
        <Textarea className="field mt-1.5 min-h-[150px]" placeholder="Paste the full job description…" value={data.job_posting} onChange={(e) => set("job_posting")(e.target.value)} data-testid="letter-posting-input" />
      </div>
      <div className="rounded-xl border border-paper-border bg-white p-5">
        <h3 className="font-serif text-lg text-ink">About you</h3>
        <Label className="mt-4 block text-xs font-semibold text-ink-soft">Write from this resume</Label>
        <Select value={data.resume_id || ""} onValueChange={set("resume_id")}>
          <SelectTrigger className="field mt-1.5" data-testid="letter-resume-select"><SelectValue placeholder={resumes.length ? "Choose a resume" : "Create a resume first"} /></SelectTrigger>
          <SelectContent>{resumes.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`letter-resume-option-${r.id}`}>{r.title}</SelectItem>)}</SelectContent>
        </Select>
        <Label className="mt-4 block text-xs font-semibold text-ink-soft">Tone</Label>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {TONES.map(([k, l]) => (
            <button key={k} onClick={() => set("tone")(k)} data-testid={`tone-${k}-btn`}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${data.tone === k ? "border-thread bg-thread text-white" : "border-paper-border bg-paper text-ink-soft hover:border-ink/30"}`}>{l}</button>
          ))}
        </div>
        <button className="btn-thread mt-5 w-full" disabled={busy || data.job_posting.trim().length < 20 || !data.resume_id} onClick={generate} data-testid="generate-letter-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Writing your letter…" : data.content ? "Rewrite with AI" : "Write my cover letter"}
        </button>
      </div>
      <div className="rounded-xl border border-paper-border bg-white p-5">
        <h3 className="font-serif text-lg text-ink">Letter</h3>
        <p className="mt-1 text-xs text-taupe">Edit freely. Leave a blank line between paragraphs.</p>
        <Textarea className={`field mt-3 min-h-[320px] leading-relaxed ${busy ? "caret" : ""}`} value={data.content} onChange={(e) => set("content")(e.target.value)} readOnly={busy} data-testid="letter-content-input" />
      </div>
    </div>
  );
};

export default function CoverLetterEditor() {
  const { id } = useParams();
  const ed = useDocEditor(id);
  const [tab, setTab] = useState("edit");
  const { doc } = ed;
  if (!doc) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-thread" /></div>;

  return (
    <div className="min-h-screen bg-paper" data-testid="cover-letter-editor">
      <AppHeader />
      <EditorToolbar doc={doc} update={ed.update} saveState={ed.saveState} onDownload={ed.download} exporting={ed.exporting} />
      <SplitLayout tab={tab} setTab={setTab}
        form={<LetterForm api={ed.api} data={doc.data} onChange={(data) => ed.update({ data })} />}
        preview={<><StylePicker type="letter" template={doc.template} accent={doc.accent} onChange={ed.update} />
          <PaperPreview watermark={!doc.can_download} testId="letter-preview" pageBreaks><LetterTemplate template={doc.template} data={doc.data} accent={doc.accent} /></PaperPreview></>}
      />
      <PricingDialog open={ed.pricingOpen} onOpenChange={ed.setPricingOpen} docId={doc.id} returnPath={`/cover-letter/${doc.id}`} beforeCheckout={ed.save} />
    </div>
  );
}
